import {
  paragraph_default
} from "./chunk-2NBVD3VO.js";
import "./chunk-U5S3T6FN.js";
import "./chunk-GJCFV3G2.js";
import {
  Model,
  addListToDropdown,
  createDropdown
} from "./chunk-CCHBLTQG.js";
import "./chunk-J3DZJUY2.js";
import {
  Collection,
  Command,
  Plugin,
  first,
  icons,
  priorities_default
} from "./chunk-XX3CIG5U.js";
import "./chunk-624QZG55.js";

// node_modules/@ckeditor/ckeditor5-paragraph/src/paragraphbuttonui.js
var icon = icons.paragraph;

// node_modules/@ckeditor/ckeditor5-heading/src/headingcommand.js
var HeadingCommand = class extends Command {
  /**
   * Creates an instance of the command.
   *
   * @param editor Editor instance.
   * @param modelElements Names of the element which this command can apply in the model.
   */
  constructor(editor, modelElements) {
    super(editor);
    this.modelElements = modelElements;
  }
  /**
   * @inheritDoc
   */
  refresh() {
    const block = first(this.editor.model.document.selection.getSelectedBlocks());
    this.value = !!block && this.modelElements.includes(block.name) && block.name;
    this.isEnabled = !!block && this.modelElements.some((heading) => checkCanBecomeHeading(block, heading, this.editor.model.schema));
  }
  /**
   * Executes the command. Applies the heading to the selected blocks or, if the first selected
   * block is a heading already, turns selected headings (of this level only) to paragraphs.
   *
   * @param options.value Name of the element which this command will apply in the model.
   * @fires execute
   */
  execute(options) {
    const model = this.editor.model;
    const document = model.document;
    const modelElement = options.value;
    model.change((writer) => {
      const blocks = Array.from(document.selection.getSelectedBlocks()).filter((block) => {
        return checkCanBecomeHeading(block, modelElement, model.schema);
      });
      for (const block of blocks) {
        if (!block.is("element", modelElement)) {
          writer.rename(block, modelElement);
        }
      }
    });
  }
};
function checkCanBecomeHeading(block, heading, schema) {
  return schema.checkChild(block.parent, heading) && !schema.isObject(block);
}

// node_modules/@ckeditor/ckeditor5-heading/src/headingediting.js
var defaultModelElement = "paragraph";
var HeadingEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "HeadingEditing";
  }
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    editor.config.define("heading", {
      options: [
        { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
        { model: "heading1", view: "h2", title: "Heading 1", class: "ck-heading_heading1" },
        { model: "heading2", view: "h3", title: "Heading 2", class: "ck-heading_heading2" },
        { model: "heading3", view: "h4", title: "Heading 3", class: "ck-heading_heading3" }
      ]
    });
  }
  /**
   * @inheritDoc
   */
  static get requires() {
    return [paragraph_default];
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const options = editor.config.get("heading.options");
    const modelElements = [];
    for (const option of options) {
      if (option.model === "paragraph") {
        continue;
      }
      editor.model.schema.register(option.model, {
        inheritAllFrom: "$block"
      });
      editor.conversion.elementToElement(option);
      modelElements.push(option.model);
    }
    this._addDefaultH1Conversion(editor);
    editor.commands.add("heading", new HeadingCommand(editor, modelElements));
  }
  /**
   * @inheritDoc
   */
  afterInit() {
    const editor = this.editor;
    const enterCommand = editor.commands.get("enter");
    const options = editor.config.get("heading.options");
    if (enterCommand) {
      this.listenTo(enterCommand, "afterExecute", (evt, data) => {
        const positionParent = editor.model.document.selection.getFirstPosition().parent;
        const isHeading = options.some((option) => positionParent.is("element", option.model));
        if (isHeading && !positionParent.is("element", defaultModelElement) && positionParent.childCount === 0) {
          data.writer.rename(positionParent, defaultModelElement);
        }
      });
    }
  }
  /**
   * Adds default conversion for `h1` -> `heading1` with a low priority.
   *
   * @param editor Editor instance on which to add the `h1` conversion.
   */
  _addDefaultH1Conversion(editor) {
    editor.conversion.for("upcast").elementToElement({
      model: "heading1",
      view: "h1",
      // With a `low` priority, `paragraph` plugin autoparagraphing mechanism is executed. Make sure
      // this listener is called before it. If not, `h1` will be transformed into a paragraph.
      converterPriority: priorities_default.low + 1
    });
  }
};

// node_modules/@ckeditor/ckeditor5-heading/src/utils.js
function getLocalizedOptions(editor) {
  const t = editor.t;
  const localizedTitles = {
    "Paragraph": t("Paragraph"),
    "Heading 1": t("Heading 1"),
    "Heading 2": t("Heading 2"),
    "Heading 3": t("Heading 3"),
    "Heading 4": t("Heading 4"),
    "Heading 5": t("Heading 5"),
    "Heading 6": t("Heading 6")
  };
  return editor.config.get("heading.options").map((option) => {
    const title = localizedTitles[option.title];
    if (title && title != option.title) {
      option.title = title;
    }
    return option;
  });
}

// node_modules/@ckeditor/ckeditor5-heading/src/headingui.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-heading/theme/heading.css";
var HeadingUI = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "HeadingUI";
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const t = editor.t;
    const options = getLocalizedOptions(editor);
    const defaultTitle = t("Choose heading");
    const accessibleLabel = t("Heading");
    editor.ui.componentFactory.add("heading", (locale) => {
      const titles = {};
      const itemDefinitions = new Collection();
      const headingCommand = editor.commands.get("heading");
      const paragraphCommand = editor.commands.get("paragraph");
      const commands = [headingCommand];
      for (const option of options) {
        const def = {
          type: "button",
          model: new Model({
            label: option.title,
            class: option.class,
            role: "menuitemradio",
            withText: true
          })
        };
        if (option.model === "paragraph") {
          def.model.bind("isOn").to(paragraphCommand, "value");
          def.model.set("commandName", "paragraph");
          commands.push(paragraphCommand);
        } else {
          def.model.bind("isOn").to(headingCommand, "value", (value) => value === option.model);
          def.model.set({
            commandName: "heading",
            commandValue: option.model
          });
        }
        itemDefinitions.add(def);
        titles[option.model] = option.title;
      }
      const dropdownView = createDropdown(locale);
      addListToDropdown(dropdownView, itemDefinitions, {
        ariaLabel: accessibleLabel,
        role: "menu"
      });
      dropdownView.buttonView.set({
        ariaLabel: accessibleLabel,
        ariaLabelledBy: void 0,
        isOn: false,
        withText: true,
        tooltip: accessibleLabel
      });
      dropdownView.extendTemplate({
        attributes: {
          class: [
            "ck-heading-dropdown"
          ]
        }
      });
      dropdownView.bind("isEnabled").toMany(commands, "isEnabled", (...areEnabled) => {
        return areEnabled.some((isEnabled) => isEnabled);
      });
      dropdownView.buttonView.bind("label").to(headingCommand, "value", paragraphCommand, "value", (value, para) => {
        const whichModel = value || para && "paragraph";
        if (typeof whichModel === "boolean") {
          return defaultTitle;
        }
        if (!titles[whichModel]) {
          return defaultTitle;
        }
        return titles[whichModel];
      });
      this.listenTo(dropdownView, "execute", (evt) => {
        const { commandName, commandValue } = evt.source;
        editor.execute(commandName, commandValue ? { value: commandValue } : void 0);
        editor.editing.view.focus();
      });
      return dropdownView;
    });
  }
};

// node_modules/@ckeditor/ckeditor5-heading/src/heading.js
import "/tmp/build_6afe4207/node_modules/@ckeditor/ckeditor5-heading/theme/heading.css";
var Heading = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [HeadingEditing, HeadingUI];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "Heading";
  }
};
export {
  Heading as default
};
/*! Bundled license information:

@ckeditor/ckeditor5-paragraph/src/paragraphbuttonui.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-paragraph/src/augmentation.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-paragraph/src/index.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

ckeditor5/src/paragraph.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-heading/src/headingcommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-heading/src/headingediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-heading/src/utils.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-heading/src/headingui.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-heading/src/heading.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=@ckeditor_ckeditor5-heading_src_heading.js.map
